import {
  queryOptions,
  skipToken,
  useQuery,
  type QueryFunctionContext,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { ms } from 'ms';

import { type UUID } from '~/types/common';

import { queryKeysCompany } from '../queryKeys';

import { fetchCompanyMoveCheck } from './fetchCompanyMoveCheck';
import { type CompanyMoveCheckResponse } from './types';

type CompanyMoveCheckOptions<TData> = Omit<
  UseQueryOptions<CompanyMoveCheckResponse, Error, TData>,
  'queryFn' | 'queryKey'
>;

/**
 * Creates reusable query options for checking a prospective company move.
 *
 * @param parameters - The optional company ID and the optional target company-account ID.
 * @param parameters.options - Additional TanStack Query options.
 * @returns Query options that remain skipped until the company ID is available.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Company/operation/check_company_move_vadmin_companies__company_id__move_check_get
 */
// fallow-ignore-next-line unused-export
export function getCompanyMoveCheckQueryOptions<
  TData = CompanyMoveCheckResponse,
>({
  companyId,
  options,
  targetCompanyAccountId,
}: {
  companyId: UUID | undefined;
  options?: CompanyMoveCheckOptions<TData>;
  targetCompanyAccountId?: UUID | undefined;
}) {
  return queryOptions({
    queryFn: companyId
      ? async ({ signal }: QueryFunctionContext) =>
          fetchCompanyMoveCheck(
            { companyId, targetCompanyAccountId },
            { signal },
          )
      : skipToken,
    queryKey: queryKeysCompany.getMoveCheck({
      companyId: companyId ?? '',
      targetCompanyAccountId,
    }),
    staleTime: ms('30s'),
    ...options,
  });
}

/**
 * Checks whether a company can move, optionally to a selected company account.
 * Without a target, only blockers on the company itself are reported.
 *
 * @param parameters - The optional company ID and the optional target company-account ID.
 * @param options - Additional TanStack Query options.
 * @returns The move-check query result.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Company/operation/check_company_move_vadmin_companies__company_id__move_check_get
 */
export function useQueryCompanyMoveCheck<TData = CompanyMoveCheckResponse>(
  {
    companyId,
    targetCompanyAccountId,
  }: {
    companyId: UUID | undefined;
    targetCompanyAccountId?: UUID | undefined;
  },
  options?: CompanyMoveCheckOptions<TData>,
) {
  return useQuery(
    getCompanyMoveCheckQueryOptions<TData>({
      companyId,
      options,
      targetCompanyAccountId,
    }),
  );
}
